import Fuse from "fuse.js";
import { pick, sortBy, uniqBy } from "lodash";
import { Moment } from "moment/moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactSelect, { ActionMeta, InputActionMeta } from "react-select";
import Select from "react-select/base";
import DatePickerModal from "../../../../components/DatePickerModal";
import useKeyboardShortcut from "../../../../components/hooks/useKeyboardShortcut";
import { getURLGridQueryParams } from "../../../../utils";
import { formatDate } from "../../util";
import { FlowState, QueryItem } from "./@types";
import { parseQuery } from "./QueryParser";
import AutosuggestMenu from "./components/AutosuggestMenu";
import MultiValueContainer from "./components/AutosuggestMultiValueContainer";
import { FLOW_STATES } from "./consts";
import { addLoadMoreOptionsIfNecessary, lintQuery } from "./utils";
import { getLabelFromQuery } from "./utils/convertQueryToString";
import {
  createNewGroupOption,
  createNewOption,
  createOptionsArrayWithComplexValue
} from "./utils/createNewOptionsUtils";
import {
  createOptionFromLiveString,
  flowStateIsCreatable,
  nextFlowState,
  optionShouldHaveGroup,
  stringIsANonKeywordArgument,
  stringIsCreatable
} from "./utils/flowControlUtils";
import {
  convertSuggestionsToOptions,
  convertSuggestionToOption,
  getSuggestionsBasedOnState
} from "./utils/suggestionUtils";

const DEFAULT_PAGE_SIZE = 50;
const DEFAULT_PAGE_SIZE_INCREMENT = 50;
const DEFAULT_HISTORY_PAGE_SIZE = 8;
const LOAD_MORE_VALUE = "__more__";
const HISTORY_PREFIX = "__history__";

interface Props {
  searchValue?: string;
  searchHistory?: Array<string>;
  onSearch?: (query: string) => void;
  onInputChange?: (newValue: string) => void;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  containerId: string;
  pageSize?: number;
  pageSizeIncrement?: number;
  historyPageSize?: number;
}

const QueryBuilderContainer = ({
  searchValue,
  searchHistory,
  onBlur,
  onFocus,
  onSearch,
  onInputChange,
  containerId,
  pageSize = DEFAULT_PAGE_SIZE,
  pageSizeIncrement = DEFAULT_PAGE_SIZE_INCREMENT,
  historyPageSize = DEFAULT_HISTORY_PAGE_SIZE
}: Props) => {
  const [flowState, setFlowState] = useState<FlowState>("ALL_KEYWORDS");
  const [inputValue, setInputValue] = useState("");
  const [value, setValue] = useState<readonly QueryItem[]>([]);
  const [stringValue, setStringValue] = useState<string>("");
  const [pageSizeState, setPageSizeState] = useState(pageSize);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [menuOpened, setMenuOpened] = useState(false);
  const [isInteractingWithMenu, setIsInteractingWithMenu] = useState(false);

  const ref = useRef<Select<QueryItem, true>>(null);
  const menuPortal = useRef<HTMLElement | null>(null);
  const userIsEditing = useRef(false);

  useKeyboardShortcut("s", { altKey: true }, () => {
    ref.current?.focus();
  });

  useEffect(() => {
    menuPortal.current = document.querySelector(
      `div#${containerId}`
    ) as HTMLElement;
  }, [containerId]);

  useEffect(() => {
    if (!searchValue) {
      setValue([]);
      setStringValue("");
      setFlowState(FLOW_STATES.ALL_KEYWORDS);
      return;
    }
    setStringValue(searchValue);
    const query = parseQuery(searchValue);
    const lastGroup = query[query.length - 1]?.grouped;
    const lastFlowState = lastGroup?.[lastGroup.length - 1]?.state;
    const lastValue = lastGroup?.[lastGroup.length - 1]?.value;
    setValue(query);
    setFlowState(
      lastFlowState && lastValue
        ? nextFlowState(lastFlowState, lastValue)
        : FLOW_STATES.ALL_KEYWORDS
    );
  }, [searchValue]);

  const allSuggestions = useMemo(
    () =>
      getSuggestionsBasedOnState({
        flowState,
        allGroups: value
      }),
    [flowState, value]
  );

  const { historyFuzzySearch, decodedHistory } = useMemo(() => {
    const urlDecodedHistory = uniqBy(
      (searchHistory || [])
        .map(item => pick(getURLGridQueryParams(item), ["searchQuery"]))
        .filter(item => !!item.searchQuery),
      "searchQuery"
    );

    return {
      historyFuzzySearch: new Fuse(urlDecodedHistory, {
        threshold: 0.3,
        keys: ["searchQuery"],
        includeMatches: true,
        ignoreLocation: true
      }),
      decodedHistory: urlDecodedHistory
    };
  }, [searchHistory]);

  const filteredSuggestions = useMemo(() => {
    const lowerCaseInputValue = inputValue.toLowerCase();
    const suggestions = allSuggestions.filter(suggestion =>
      suggestion.value.toLowerCase().includes(lowerCaseInputValue)
    );

    // rank suggestions that start with the input value first
    const sortedSuggestions = sortBy(
      suggestions,
      suggestion =>
        !suggestion.value.toLowerCase().startsWith(lowerCaseInputValue)
    );

    return addLoadMoreOptionsIfNecessary(
      convertSuggestionsToOptions(inputValue, flowState, sortedSuggestions),
      pageSizeState,
      LOAD_MORE_VALUE
    );
  }, [inputValue, flowState, allSuggestions, pageSizeState]);

  const historySuggestions = useMemo(() => {
    const query = `${stringValue} ${inputValue}`.trim();

    if (!query)
      return convertSuggestionsToOptions(
        query,
        flowState,
        decodedHistory.map(item => ({
          label: item.searchQuery,
          value: `${HISTORY_PREFIX}${item.searchQuery}`
        })),
        false
      ).slice(0, historyPageSize);

    const suggestions = historyFuzzySearch.search(query);
    return addLoadMoreOptionsIfNecessary(
      suggestions.map(({ item }) =>
        convertSuggestionToOption(
          inputValue,
          flowState,
          {
            value: `${HISTORY_PREFIX}${item.searchQuery}`,
            label: item.searchQuery
          },
          false
        )
      ),
      pageSizeState,
      LOAD_MORE_VALUE
    );
  }, [
    stringValue,
    inputValue,
    flowState,
    decodedHistory,
    historyPageSize,
    historyFuzzySearch,
    pageSizeState
  ]);

  const suggestionsAreFirst = inputValue.length || value.length;
  const options = suggestionsAreFirst
    ? [
        {
          label: "Suggestions",
          options: filteredSuggestions
        },
        {
          label: "Recent Queries",
          options: historySuggestions
        }
      ]
    : [
        {
          label: "Recent Queries",
          options: historySuggestions
        },
        {
          label: "Suggestions",
          options: filteredSuggestions
        }
      ];

  const isValidNewOption = (newOptionInput: string) =>
    stringIsANonKeywordArgument(newOptionInput, flowState) ||
    (flowStateIsCreatable(flowState) &&
      stringIsCreatable(
        newOptionInput,
        flowState,
        value[value.length - 1]?.grouped
      ));

  const setValueWithInputChange = (
    func: (prev: readonly QueryItem[]) => readonly QueryItem[],
    shouldTriggerSearch = false
  ) => {
    const newValue = func(value);
    const newInputValue = getLabelFromQuery(newValue);
    onInputChange?.(newInputValue);
    if (shouldTriggerSearch) onSearch?.(newInputValue);
    setValue(newValue);
    setStringValue(newInputValue);
  };

  const handleChange = (
    newValue: readonly QueryItem[],
    actionMeta: ActionMeta<QueryItem>,
    shouldTriggerSearch = false
  ) => {
    if (actionMeta.option?.value.trim() === "") return;

    if (!isInteractingWithMenu && actionMeta.action === "select-option") return;

    if (
      actionMeta.option?.value === LOAD_MORE_VALUE &&
      actionMeta.action === "select-option"
    ) {
      setPageSizeState(prev =>
        Math.min(prev + pageSizeIncrement, allSuggestions.length)
      );
      setIsInteractingWithMenu(true);
      setTimeout(() => {
        ref.current?.setState({
          focusedOption:
            ref.current?.getFocusableOptionsWithIds()?.[
              filteredSuggestions.length -
                1 +
                (suggestionsAreFirst ? 0 : historySuggestions.length)
            ]?.data ?? null
        });
        if (ref.current) ref.current.scrollToFocusedOptionOnUpdate = true;
      }, 0);
      return;
    }
    setPageSizeState(pageSize);
    setInputValue("");

    if (
      actionMeta.option?.value.endsWith("__datepicker") &&
      actionMeta.action === "select-option"
    ) {
      ref.current?.blur();
      setMenuOpened(false);
      setShowDatePicker(true);
      setInputValue("");
      return;
    }

    if (
      actionMeta.action === "select-option" &&
      actionMeta.option?.value.startsWith(HISTORY_PREFIX)
    ) {
      const newOption = createNewGroupOption({
        value: actionMeta.option.value.slice(HISTORY_PREFIX.length),
        flowState
      });
      setValueWithInputChange(() => [newOption], true);
      setInputValue("");
      ref.current?.blur();
      return;
    }

    setIsInteractingWithMenu(false);

    if (
      (actionMeta.action === "select-option" ||
        actionMeta.action === "create-option") &&
      actionMeta.option
    ) {
      if (optionShouldHaveGroup(actionMeta.option)) {
        // add new group option to the value
        const newGroup = createNewGroupOption({
          value: actionMeta.option.value,
          flowState: actionMeta.option.state
        });
        setValueWithInputChange(
          prevState => lintQuery([...prevState, newGroup]),
          shouldTriggerSearch
        );
      } else {
        // add the option to the last group of the value
        const newOptions = createOptionsArrayWithComplexValue({
          currentGroup: value[value.length - 1].grouped,
          optionToAdd: actionMeta.option,
          flowState
        });
        const newGroup = [
          ...(value[value.length - 1].grouped ?? []),
          ...newOptions
        ];
        const newGroupLabel = getLabelFromQuery(newGroup);
        setValueWithInputChange(
          prev => [
            ...prev.slice(0, prev.length - 1),
            {
              ...prev[prev.length - 1],
              value: newGroupLabel,
              label: newGroupLabel,
              grouped: newGroup
            }
          ],
          shouldTriggerSearch
        );

        if (actionMeta.option.complexValue) {
          setFlowState(
            nextFlowState(
              newGroup[newGroup.length - 1].state,
              newGroup[newGroup.length - 1].value
            )
          );
          return;
        }
      }
      setFlowState(
        nextFlowState(flowState, newValue[newValue.length - 1].value)
      );
    } else if (
      actionMeta.action === "remove-value" ||
      actionMeta.action === "deselect-option"
    ) {
      // set the value to the new value and update the flow state to the next state from the last element
      const lintedQuery = lintQuery(newValue);
      setValueWithInputChange(() => lintedQuery, shouldTriggerSearch);
      if (lintedQuery.length === 0) {
        setFlowState(FLOW_STATES.ALL_KEYWORDS);
      } else {
        const lastGroup = lintedQuery.at(-1);
        const lastGroupElement = lastGroup?.grouped?.at(-1);
        if (!lastGroupElement) {
          setFlowState(FLOW_STATES.ALL_KEYWORDS);
          return;
        }
        setFlowState(
          nextFlowState(lastGroupElement.state, lastGroupElement.value)
        );
      }
    } else if (actionMeta.action === "pop-value") {
      const lastGroup = actionMeta.removedValue;
      if (!lastGroup) {
        setValueWithInputChange(() => [], shouldTriggerSearch);
        setFlowState(FLOW_STATES.ALL_KEYWORDS);
        return;
      }

      if (!lastGroup.grouped) {
        // if grouped is not defined, remove the last group
        setValueWithInputChange(
          prev => prev.slice(0, prev.length - 1),
          shouldTriggerSearch
        );
        setInputValue(lastGroup.value);
      } else if (lastGroup.grouped.length > 1) {
        // if the last group has more than one item, remove the last element of the group
        const newLastGroup = lastGroup.grouped.slice(0, -1);
        const newGroupLabel = getLabelFromQuery(newLastGroup);
        setValueWithInputChange(
          prev => [
            ...prev.slice(0, prev.length - 1),
            {
              ...prev[prev.length - 1],
              value: newGroupLabel,
              label: newGroupLabel,
              grouped: newLastGroup
            }
          ],
          shouldTriggerSearch
        );
        setInputValue(lastGroup.grouped[lastGroup.grouped.length - 1].value);
        setFlowState(lastGroup.grouped[lastGroup.grouped.length - 1].state);
      } else {
        // if the last group has only one item, remove the last group
        setValueWithInputChange(
          prev => prev.slice(0, prev.length - 1),
          shouldTriggerSearch
        );
        setInputValue(lastGroup.grouped[0].value);
        setFlowState(lastGroup.grouped[0].state);
      }
    } else {
      setFlowState(FLOW_STATES.ALL_KEYWORDS);
      setValueWithInputChange(() => [], shouldTriggerSearch);
    }
  };

  const handleOnInputChange = (
    newValue: string,
    actionMeta: InputActionMeta
  ) => {
    const userPasted = newValue.length - actionMeta.prevInputValue.length > 1;

    const newOption = createOptionFromLiveString(newValue, flowState);

    if (newOption) {
      handleChange([...value, newOption], {
        action: "create-option",
        option: newOption
      });
      setInputValue("");
      return;
    } else if (userPasted && flowState === FLOW_STATES.ALL_KEYWORDS) {
      const parsedQuery = parseQuery(newValue);
      setValueWithInputChange(() => [...value, ...parsedQuery]);
      setInputValue("");
      setFlowState(
        nextFlowState(
          parsedQuery.at(-1)?.grouped?.at(-1)?.state ?? "ALL_KEYWORDS",
          parsedQuery.at(-1)?.grouped?.at(-1)?.value
        )
      );
    }

    if (actionMeta.action !== "set-value") {
      setPageSizeState(pageSize);
      if (
        actionMeta.action !== "input-blur" &&
        actionMeta.action !== "menu-close" &&
        !(userPasted && flowState === FLOW_STATES.ALL_KEYWORDS)
      )
        setInputValue(newValue);
    }
  };

  const handleOnKeyDown: React.KeyboardEventHandler<HTMLDivElement> = event => {
    const preventEventTriggering = () => {
      event.preventDefault();
      event.stopPropagation();
    };

    if (event.key === "Enter" && !isInteractingWithMenu) {
      preventEventTriggering();
      ref.current?.blur();
      setMenuOpened(false);
      setIsInteractingWithMenu(false);
      const trimmedInputValue = inputValue.trim();
      if (!trimmedInputValue) {
        onSearch?.(getLabelFromQuery(value));
        return;
      }

      if (isValidNewOption(trimmedInputValue)) {
        const newOption = createNewOption({
          value: trimmedInputValue,
          flowState,
          useValueAsItemKey: true
        });
        handleChange(
          [...value, newOption],
          {
            action: "create-option",
            option: newOption
          },
          true
        );
        setInputValue("");
        return;
      }
    }

    if (event.key === "Tab" && !isInteractingWithMenu) {
      preventEventTriggering();
      if (isValidNewOption(inputValue)) {
        const newOption = createNewOption({
          value: inputValue,
          flowState,
          useValueAsItemKey: true
        });
        handleChange([...value, newOption], {
          action: "create-option",
          option: newOption
        });
        setInputValue("");
      }
    }

    if (
      event.key === "Backspace" &&
      event.ctrlKey &&
      !isInteractingWithMenu &&
      !inputValue
    ) {
      preventEventTriggering();
      handleChange(value.slice(0, -1), {
        action: "remove-value",
        removedValue: value[value.length - 1]
      });
    }

    switch (event.key) {
      case "ArrowDown":
        if (!isInteractingWithMenu) {
          preventEventTriggering();
          ref.current?.focusOption("first");
        }
        setIsInteractingWithMenu(true);
        break;
      case "ArrowUp":
        if (
          isInteractingWithMenu &&
          ref.current?.focusedOptionRef?.id ===
            ref.current?.getFocusableOptionsWithIds()[0]?.id
        ) {
          preventEventTriggering();
          ref.current?.focusInput();
          setIsInteractingWithMenu(false);
          break;
        }

        if (!isInteractingWithMenu) {
          preventEventTriggering();
          ref.current?.focusOption("last");
        }
        setIsInteractingWithMenu(true);
        break;
      case "Escape":
        ref.current?.blur();
        setMenuOpened(false);
        setIsInteractingWithMenu(false);
        break;
      default:
        setIsInteractingWithMenu(false);
        break;
    }
  };

  const handleFocus: React.FocusEventHandler<HTMLInputElement> = e => {
    e.preventDefault();
    e.stopPropagation();
    setMenuOpened(true);
    setIsInteractingWithMenu(false);
    onFocus?.(e);
  };

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = e => {
    e.preventDefault();
    e.stopPropagation();
    setMenuOpened(false);
    setIsInteractingWithMenu(false);
    if (inputValue.trim() && !userIsEditing.current) {
      const newOption = createNewOption({
        value: inputValue,
        flowState,
        useValueAsItemKey: true
      });
      handleChange([...value, newOption], {
        action: "create-option",
        option: newOption
      });
    }
    userIsEditing.current = false;
    onBlur?.(e);
  };

  const handleEditValue = (
    prevOption: QueryItem,
    newOption: QueryItem | null
  ) => {
    const newValue = newOption
      ? value.map(preVal =>
          preVal.itemKey === prevOption.itemKey ? newOption : preVal
        )
      : value.filter(preVal => preVal.itemKey !== prevOption.itemKey);
    handleChange(newValue, {
      action: "remove-value",
      removedValue: prevOption
    });
    ref.current?.focus();
    setMenuOpened(true);
    setIsInteractingWithMenu(false);
  };

  const handleAddEmptyValue = (
    pivotValue: QueryItem,
    position: "before" | "after"
  ) => {
    const newGroup = createNewGroupOption({
      value: "",
      flowState: FLOW_STATES.GROUP,
      isEditing: true
    });
    userIsEditing.current = true;
    if (inputValue.trim()) {
      const newOption = createNewOption({
        value: inputValue,
        flowState,
        useValueAsItemKey: true
      });
      handleChange([...value, newOption], {
        action: "create-option",
        option: newOption
      });
    }
    setValue(prev => {
      const index = prev.findIndex(val => val.itemKey === pivotValue.itemKey);
      return [
        ...prev.slice(0, index + (position === "before" ? 0 : 1)),
        newGroup,
        ...prev.slice(index + (position === "before" ? 0 : 1))
      ];
    });
  };

  const handleOptionClick = (option: QueryItem) => {
    userIsEditing.current = true;
    if (inputValue.trim()) {
      const newOption = createNewOption({
        value: inputValue,
        flowState,
        useValueAsItemKey: true
      });
      handleChange([...value, newOption], {
        action: "create-option",
        option: newOption
      });
    }
    setValue(prev => {
      const index = prev.findIndex(val => val.itemKey === option.itemKey);
      if (index === -1) return prev;

      return [
        ...prev.slice(0, index),
        {
          ...prev[index],
          isEditing: !prev[index].isEditing
        },
        ...prev.slice(index + 1)
      ];
    });
  };

  return (
    <>
      <ReactSelect<QueryItem, true>
        ref={ref}
        onFocus={handleFocus}
        onKeyDown={handleOnKeyDown}
        onBlur={handleBlur}
        closeMenuOnSelect={false}
        getOptionValue={option => option.itemKey}
        menuIsOpen={
          menuOpened &&
          (filteredSuggestions.length > 0 || historySuggestions.length > 0)
        }
        inputValue={inputValue}
        menuPortalTarget={menuPortal.current}
        onMenuOpen={() => setPageSizeState(pageSize)}
        classNames={{
          input: () => "autosuggest-select-input"
        }}
        tabSelectsValue
        isClearable
        maxMenuHeight={500}
        isMulti
        onChange={handleChange}
        onInputChange={handleOnInputChange}
        placeholder={"Search [Alt+S]"}
        value={value}
        filterOption={() => true}
        options={options}
        styles={{
          container: base => ({
            ...base,
            maxWidth: "100%"
          }),
          control: base => ({
            ...base,
            width: "100%",
            border: "none",
            borderColor: "transparent",
            boxShadow: "none",
            overflow: "hidden",
            flexWrap: "nowrap",
            height: "inherit"
          }),
          valueContainer: base => ({
            ...base,
            msOverflowStyle: "none",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none"
            },
            overflow: "auto",
            flexWrap: "nowrap"
          }),
          option: (base, optionProps) => ({
            ...base,
            padding: 0,
            margin: 0,
            backgroundColor:
              optionProps.isFocused && isInteractingWithMenu
                ? base.backgroundColor
                : "transparent"
          }),
          menuPortal: base => ({ ...base, left: 0, top: 40, width: "100%" }),
          menuList: base => ({
            ...base,
            overflowX: "hidden"
          }),
          multiValueLabel: base => ({
            ...base,
            whiteSpace: "nowrap",
            fontSize: "13px"
          }),
          input: base => ({
            ...base,
            fontSize: "13px"
          })
        }}
        components={{
          MultiValueContainer,
          Menu: AutosuggestMenu,
          DropdownIndicator: null,
          IndicatorSeparator: null
        }}
        onEditValue={handleEditValue}
        onAddEmptyValue={handleAddEmptyValue}
        onMenuMouseLeave={() => setIsInteractingWithMenu(false)}
        onMenuMouseEnter={() => setIsInteractingWithMenu(true)}
        onOptionClick={handleOptionClick}
      />
      <DatePickerModal
        show={showDatePicker}
        onClose={setShowDatePicker}
        onApply={({ start, end }: { start: Moment; end: Moment }) => {
          const formatedStart = formatDate(start);
          const formatedEnd = formatDate(end);
          const timeValue = `[${formatedStart} TO ${formatedEnd}]`;
          handleChange(value, {
            action: "create-option",
            option: createNewOption({
              value: timeValue,
              flowState,
              useValueAsItemKey: true,
              complexValue: [formatedStart, "TO", formatedEnd, "]"]
            })
          });
          setShowDatePicker(false);
          setIsInteractingWithMenu(false);
        }}
      />
    </>
  );
};

export default QueryBuilderContainer;
